import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../App';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  MessageCircle,
  Plus,
  Send,
  Clock,
  CheckCircle,
  AlertCircle,
  Loader2,
  ChevronRight,
  ArrowLeft,
  HelpCircle,
  Bug,
  Lightbulb,
  CreditCard,
  MoreHorizontal
} from 'lucide-react';

const Support = () => {
  const { t } = useTranslation();
  const { token, API } = useContext(AuthContext);
  const { toast } = useToast();

  // State
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [replyMessage, setReplyMessage] = useState('');
  const [sendingReply, setSendingReply] = useState(false);

  // New ticket form
  const [newTicket, setNewTicket] = useState({
    subject: '',
    message: '',
    category: 'general',
    priority: 'normal'
  });

  const categories = [
    { value: 'general', label: t('support.categoryGeneral'), icon: HelpCircle },
    { value: 'bug', label: t('support.categoryBug'), icon: Bug },
    { value: 'feature', label: t('support.categoryFeature'), icon: Lightbulb },
    { value: 'billing', label: t('support.categoryBilling'), icon: CreditCard },
    { value: 'other', label: t('support.categoryOther'), icon: MoreHorizontal }
  ];

  const priorities = [
    { value: 'low', label: t('support.priorityLow'), color: 'bg-gray-100 text-gray-700' },
    { value: 'normal', label: t('support.priorityNormal'), color: 'bg-blue-100 text-blue-700' },
    { value: 'high', label: t('support.priorityHigh'), color: 'bg-orange-100 text-orange-700' },
    { value: 'urgent', label: t('support.priorityUrgent'), color: 'bg-red-100 text-red-700' }
  ];

  const statuses = {
    open: { label: t('support.statusOpen'), color: 'bg-yellow-100 text-yellow-700', icon: Clock },
    in_progress: { label: t('support.statusInProgress'), color: 'bg-blue-100 text-blue-700', icon: Loader2 },
    resolved: { label: t('support.statusResolved'), color: 'bg-green-100 text-green-700', icon: CheckCircle },
    closed: { label: t('support.statusClosed'), color: 'bg-gray-100 text-gray-700', icon: CheckCircle }
  };

  // Fetch tickets
  const fetchTickets = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/support/tickets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTickets(response.data);
    } catch (error) {
      console.error('Error fetching tickets:', error);
      toast({
        title: t('common.error'),
        description: t('support.errorLoadingTickets'),
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, []);

  // Create new ticket
  const handleCreateTicket = async (e) => {
    e.preventDefault();
    if (!newTicket.subject.trim() || !newTicket.message.trim()) {
      toast({
        title: t('common.error'),
        description: t('support.fillAllFields'),
        variant: 'destructive'
      });
      return;
    }

    try {
      setSubmitting(true);
      const response = await axios.post(`${API}/support/tickets`, newTicket, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setTickets([response.data, ...tickets]);
      setShowNewTicket(false);
      setNewTicket({ subject: '', message: '', category: 'general', priority: 'normal' });
      
      toast({
        title: t('common.success'),
        description: t('support.ticketCreated')
      });
    } catch (error) {
      console.error('Error creating ticket:', error);
      toast({
        title: t('common.error'),
        description: t('support.errorCreatingTicket'),
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Send reply
  const handleSendReply = async () => {
    if (!replyMessage.trim() || !selectedTicket) return;

    try {
      setSendingReply(true);
      const response = await axios.post(
        `${API}/support/tickets/${selectedTicket.id}/reply`,
        { message: replyMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSelectedTicket(response.data);
      setTickets(tickets.map(t => t.id === response.data.id ? response.data : t));
      setReplyMessage('');
      
      toast({
        title: t('common.success'),
        description: t('support.replySent')
      });
    } catch (error) {
      console.error('Error sending reply:', error);
      toast({
        title: t('common.error'),
        description: t('support.errorSendingReply'),
        variant: 'destructive'
      });
    } finally {
      setSendingReply(false);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Ticket list view
  const renderTicketList = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('support.title')}</h1>
          <p className="text-gray-600">{t('support.subtitle')}</p>
        </div>
        <Button onClick={() => setShowNewTicket(true)} className="flex items-center gap-2">
          <Plus className="w-4 h-4" />
          {t('support.newTicket')}
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : tickets.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <MessageCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">{t('support.noTickets')}</h3>
            <p className="text-gray-600 mb-4">{t('support.noTicketsDesc')}</p>
            <Button onClick={() => setShowNewTicket(true)}>
              {t('support.createFirstTicket')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {tickets.map((ticket) => {
            const status = statuses[ticket.status] || statuses.open;
            const StatusIcon = status.icon;
            return (
              <Card
                key={ticket.id}
                className="cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => setSelectedTicket(ticket)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={status.color}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {status.label}
                        </Badge>
                        <Badge variant="outline">
                          {categories.find(c => c.value === ticket.category)?.label || ticket.category}
                        </Badge>
                      </div>
                      <h3 className="font-medium text-gray-900">{ticket.subject}</h3>
                      <p className="text-sm text-gray-500 mt-1">
                        {formatDate(ticket.created_at)} • {ticket.messages?.length || 1} {t('support.messages')}
                      </p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );

  // Ticket detail view
  const renderTicketDetail = () => {
    if (!selectedTicket) return null;
    const status = statuses[selectedTicket.status] || statuses.open;
    const StatusIcon = status.icon;

    return (
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => setSelectedTicket(null)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('common.back')}
          </Button>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-start justify-between">
              <div>
                <CardTitle>{selectedTicket.subject}</CardTitle>
                <CardDescription className="mt-1">
                  {t('support.ticketId')}: {selectedTicket.id.slice(0, 8)}... • {formatDate(selectedTicket.created_at)}
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Badge className={status.color}>
                  <StatusIcon className="w-3 h-3 mr-1" />
                  {status.label}
                </Badge>
                <Badge variant="outline">
                  {categories.find(c => c.value === selectedTicket.category)?.label}
                </Badge>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Messages */}
            <div className="space-y-4 mb-6 max-h-96 overflow-y-auto">
              {selectedTicket.messages?.map((message, idx) => (
                <div
                  key={message.id || idx}
                  className={`p-4 rounded-lg ${
                    message.sender_type === 'admin'
                      ? 'bg-blue-50 border-l-4 border-blue-500'
                      : 'bg-gray-50'
                  }`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <span className="font-medium text-gray-900">
                      {message.sender_type === 'admin' ? t('support.supportTeam') : message.sender_name}
                    </span>
                    <span className="text-xs text-gray-500">{formatDate(message.created_at)}</span>
                  </div>
                  <p className="text-gray-700 whitespace-pre-wrap">{message.message}</p>
                </div>
              ))}
            </div>

            {/* Reply form - only if ticket is not closed */}
            {selectedTicket.status !== 'closed' && (
              <div className="border-t pt-4">
                <Textarea
                  placeholder={t('support.replyPlaceholder')}
                  value={replyMessage}
                  onChange={(e) => setReplyMessage(e.target.value)}
                  rows={3}
                  className="mb-3"
                />
                <Button
                  onClick={handleSendReply}
                  disabled={sendingReply || !replyMessage.trim()}
                  className="flex items-center gap-2"
                >
                  {sendingReply ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  {t('support.sendReply')}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  };

  // New ticket dialog
  const renderNewTicketDialog = () => (
    <Dialog open={showNewTicket} onOpenChange={setShowNewTicket}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('support.newTicket')}</DialogTitle>
          <DialogDescription>{t('support.newTicketDesc')}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleCreateTicket} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('support.category')}
            </label>
            <div className="grid grid-cols-2 gap-2">
              {categories.map((cat) => {
                const Icon = cat.icon;
                return (
                  <button
                    key={cat.value}
                    type="button"
                    onClick={() => setNewTicket({ ...newTicket, category: cat.value })}
                    className={`flex items-center gap-2 p-3 rounded-lg border transition-colors ${
                      newTicket.category === cat.value
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="text-sm">{cat.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('support.subject')}
            </label>
            <Input
              value={newTicket.subject}
              onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
              placeholder={t('support.subjectPlaceholder')}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('support.message')}
            </label>
            <Textarea
              value={newTicket.message}
              onChange={(e) => setNewTicket({ ...newTicket, message: e.target.value })}
              placeholder={t('support.messagePlaceholder')}
              rows={5}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('support.priority')}
            </label>
            <div className="flex gap-2">
              {priorities.map((p) => (
                <button
                  key={p.value}
                  type="button"
                  onClick={() => setNewTicket({ ...newTicket, priority: p.value })}
                  className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                    newTicket.priority === p.value
                      ? p.color + ' ring-2 ring-offset-1 ring-blue-500'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setShowNewTicket(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('common.submitting')}
                </>
              ) : (
                t('support.submitTicket')
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {selectedTicket ? renderTicketDetail() : renderTicketList()}
      {renderNewTicketDialog()}
    </div>
  );
};

export default Support;
